import type { FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import BioscopeLine from '@/assets/icons/bioscope_line.svg?component';
import CornerDownRightLine from '@/assets/icons/corner_down_right_line.svg?component';
import Scales3Line from '@/assets/icons/ri_scales_3_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import Tooltip from '@/components/atoms/Tooltip';

import type { FocusSource } from '../../helpers/lineFocus';
import { rowWeightLabel } from '../../helpers/weightText';
import type { BlockTransfer, GroupRowModel } from '../../types';
import Glyph from '../Glyph/Glyph';
import { TRACK_TIP_DELAY } from '../GroupTrack/TrackTip';

import ChainMenu from './ChainMenu';
import OriginToggle from './OriginToggle';

import styles from './GroupLabel.module.scss';

interface GroupLabelProps {
    row: GroupRowModel;
    /** The block's sources are on screen; drives the Origin toggle's pressed state. */
    sourcesShown: boolean;
    onToggleOrigin?: () => void;
    /** Opens the row's group's dialog; the name is then its button. */
    onOpen?: () => void;
    /** Opens an earlier group of a chain row, from the "N groups" menu. */
    onOpenMember?: (groupId: number) => void;
    /** The transfers into the row from other rows, for the Origin tooltip. */
    origins?: BlockTransfer[];
    /** Earlier rows its Origin kept because other rows share them. */
    sharedCount?: number;
    /** Its lineage is lit and the other rows dimmed. */
    highlighted?: boolean;
    /**
     * The pointer, or keyboard focus, is on the name cell (true) or has left it (false); each
     * is reported on its own, so one leaving doesn't clear the other.
     */
    onFocusChange?: (source: FocusSource, focused: boolean) => void;
}

export default function GroupLabel(props: GroupLabelProps) {
    const { row, sourcesShown, onToggleOrigin, onOpen, onOpenMember } = props;
    const { origins = [], sharedCount = 0, highlighted = false, onFocusChange } = props;
    // Moving focus between the cell's own buttons keeps it focused.
    const blur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onFocusChange?.('keyboard', false);
    };
    const { t, i18n } = useTranslation('fishGroups');

    const bioscopes =
        row.bioscopeIds.length > 0 ? row.bioscopeIds.join(', ') : t('board.row.noBioscope');
    // The tree connector points at sources, so it goes when they are hidden.
    const showsConnector = row.role === 'result' && sourcesShown;
    const weight = rowWeightLabel(
        row.weight,
        row.endedKind ? t(`board.ended.${row.endedKind}`) : null,
        i18n.language,
        t
    );

    return (
        <div
            className={classNames(styles.label, {
                [styles['label--source']]: row.role === 'source',
                [styles['label--result']]: showsConnector,
            })}
            onMouseEnter={() => onFocusChange?.('pointer', true)}
            onMouseLeave={() => onFocusChange?.('pointer', false)}
            onFocus={() => onFocusChange?.('keyboard', true)}
            onBlur={blur}>
            <div className={styles.nameLine}>
                {showsConnector && (
                    <span className={styles.connector}>
                        <Glyph icon={CornerDownRightLine} />
                    </span>
                )}
                {onOpen ? (
                    <Button variant="link" className={styles.name} onClick={onOpen}>
                        {row.name}
                    </Button>
                ) : (
                    <span className={styles.name}>{row.name}</span>
                )}
                {row.members.length > 1 && <ChainMenu row={row} onOpenMember={onOpenMember} />}
                {onToggleOrigin && row.originCount > 0 && (
                    <OriginToggle
                        count={row.originCount}
                        pressed={sourcesShown}
                        origins={origins}
                        sharedCount={sharedCount}
                        highlighted={highlighted}
                        onToggle={onToggleOrigin}
                    />
                )}
            </div>
            <div className={styles.meta}>
                <Tooltip content={weight.title} layout="inline" delay={TRACK_TIP_DELAY}>
                    <span className={styles.metaItem}>
                        <Glyph icon={Scales3Line} />
                        <span className={styles.visuallyHidden}>{t('board.row.weight')}: </span>
                        {weight.text}
                    </span>
                </Tooltip>
                <Tooltip content={t('board.row.bioscope')} layout="inline" delay={TRACK_TIP_DELAY}>
                    <span className={styles.metaItem}>
                        <Glyph icon={BioscopeLine} />
                        <span className={styles.visuallyHidden}>{t('board.row.bioscope')}: </span>
                        {bioscopes}
                    </span>
                </Tooltip>
            </div>
        </div>
    );
}
